import { useMemo } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { Row } from '@tanstack/react-table'

import { InputFormValues } from '@/modules/CombinedLogs/types'
import { useCombinedLogsTableFormStateStore } from '@/modules/CombinedLogs/store'
import { useWorkstationStore } from '@/modules/Workstation/store'
import { getFormattedDate } from '@/helpers/time'
import { LogAction } from '../../information/LogAction'
import { getMandatoryFieldsForLog } from '@/modules/CombinedLogs/helpers/getMandatoryFieldsForLog'
import { Log, LogStatus, MandatoryLogInformation } from '@/types/logs'
import { DEFAULT_CODING_PERIOD } from '@/modules/CombinedLogs/constant'

interface Props {
    form: UseFormReturn<InputFormValues>
    isRowSelected: boolean
    selectedRow?: Row<Log>
}

export const TableFormLogActionInput = ({ form, isRowSelected, selectedRow }: Props) => {
    const { reason, code, route, trip, selectedTrips, report, mechanicalInfo, setReport, setMechanicalInformation } =
        useCombinedLogsTableFormStateStore()
    const { workstation } = useWorkstationStore()

    const formCode = form.watch('code')
    const formRoutes = form.watch('routes')
    const fleet = form.watch('fleet')
    const run = form.watch('run')
    const description = form.watch('description')
    const extraInfo = form.watch('extraInfo')
    const duty = form.watch('duty')
    const delay = form.watch('delay')
    const time = form.watch('timeStamp')
    const locationFrom = form.watch('locationFrom')
    const locationTo = form.watch('locationTo')
    const isZeroMileage = form.watch('isZeroMileage')

    const log = useMemo(() => {
        const selectedLog = selectedRow?.original

        const result = {
            id: selectedLog?.id,
            author: '',
            code: code || formCode || selectedLog?.code,
            codingPeriodInfo: selectedLog?.codingPeriodInfo || DEFAULT_CODING_PERIOD,
            delayInMinutes: delay || selectedLog?.delayInMinutes,
            duty: duty || selectedLog?.duty ? (duty ? [duty] : selectedLog?.duty) : undefined,
            fleet: fleet || selectedLog?.fleet ? (fleet ? [fleet] : selectedLog?.duty) : undefined,
            routes: (route ?? formRoutes) ? [route ?? formRoutes] : (selectedLog?.routes ?? undefined),
            run: run || selectedLog?.run,
            trip: trip || selectedLog?.trip,
            rtnTrips: selectedTrips,
            report,
            mechanicalInfo,
            codeTypeReasonInfo: reason,
            locationFrom,
            locationTo,
            descriptionInfo: {
                description: description || selectedLog?.descriptionInfo.description,
                extraInfo: extraInfo || selectedLog?.descriptionInfo.extraInfo,
            },
            workstationNumber: workstation?.workstationNumber,
            timestamp: getFormattedDate({
                time,
            }),
            isZeroMileage: isZeroMileage === 'true',
        } as unknown as Log

        const requiredFields = getMandatoryFieldsForLog(result)

        const hasMissingData = requiredFields.some((field) => {
            if (field === MandatoryLogInformation.Description || field === MandatoryLogInformation.ExtraInfo) {
                return !result.descriptionInfo[field]
            }

            return !result[field]
        })

        result.status = hasMissingData ? LogStatus.Incomplete : LogStatus.Completed

        return result
    }, [
        selectedRow,
        code,
        formCode,
        route,
        formRoutes,
        duty,
        fleet,
        route,
        run,
        trip,
        selectedTrips,
        reason,
        report,
        mechanicalInfo,
        description,
        locationFrom,
        locationTo,
        extraInfo,
    ])

    const callbackWrapper = <T,>(callback: (data: T | null) => void) => {
        if (selectedRow) {
            return
        }

        return (data?: T | null) => {
            callback(data as T | null)

            form.setValue('code', formCode, { shouldDirty: true })
        }
    }

    return (
        <LogAction
            log={log}
            isRowSelected={isRowSelected}
            submitOfficialReportCallback={callbackWrapper(setReport)}
            submitMechanicalInformationCallback={callbackWrapper(setMechanicalInformation)}
            deleteOfficialReportCallback={callbackWrapper(() => setReport(null))}
            deleteMechanicalInformationCallback={callbackWrapper(() => setMechanicalInformation(null))}
        />
    )
}
